import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { APP_ROUTES } from '../routing/appRoutes';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: string;
  requiredPermission?: string;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredRole, 
  requiredPermission 
}) => {
  const { user, isAuthenticated, loading } = useAuth();
  const location = useLocation();
  const { t } = useTranslation();

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        flexDirection="column"
      >
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          {t('common.loading')}
        </Typography>
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={APP_ROUTES.auth.login} state={{ from: location }} replace />;
  }

  if (requiredRole && user?.role !== 'SUPERUSER' && user?.role !== requiredRole) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        flexDirection="column"
      >
        <Typography variant="h4" color="error">
          {t('auth.accessDenied')}
        </Typography>
        <Typography variant="body1" sx={{ mt: 2 }}>
          {t('auth.permissionDenied')}
        </Typography>
      </Box>
    );
  }

  if (requiredPermission && user?.role !== 'SUPERUSER') {
    const hasPermission = Boolean(user?.permissions && (user.permissions as any)[requiredPermission]);
    if (!hasPermission) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="100vh"
          flexDirection="column"
        >
          <Typography variant="h4" color="error">
            {t('auth.accessDenied')}
          </Typography>
          <Typography variant="body1" sx={{ mt: 2 }}>
            {t('auth.requiredPermissionDenied')}
          </Typography>
        </Box>
      );
    }
  }

  return <>{children}</>;
};
